import React, { useState, useEffect } from 'react';
import api from '../../api';
import toastService from '../../services/toastService';
import {
  Settings,
  AlertTriangle,
  Bell,
  Users,
  Save,
  Plus,
  X,
  Brain,
  TrendingUp,
  TrendingDown,
  Shield
} from 'lucide-react';

interface RiskAppetiteThreshold {
  id?: number;
  project_id?: number;
  threshold_name: string;
  risk_category: string;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  threshold_value: number;
  threshold_type: 'count' | 'percentage' | 'score' | 'monetary';
  metric_target: 'high_risks' | 'control_gaps' | 'residual_risk_score' | 'control_effectiveness';
  measurement_period: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  tolerance_buffer: number;
  auto_escalate: boolean;
  notification_enabled: boolean;
  escalation_recipients: string[];
  business_unit?: string;
  created_at?: string;
  updated_at?: string;
}

interface NotificationRule {
  id?: number;
  threshold_id: number;
  trigger_condition: 'threshold_exceeded' | 'trend_negative' | 'consecutive_breaches';
  notification_type: 'email' | 'dashboard' | 'webhook';
  recipients: string[];
  message_template: string;
  cooldown_period: number;
  active: boolean;
}

interface RiskAppetiteConfigProps {
  projectId?: string;
  onConfigChange?: (config: any) => void;
}

export const RiskAppetiteConfig: React.FC<RiskAppetiteConfigProps> = ({
  projectId: propProjectId,
  onConfigChange
}) => {
  const [thresholds, setThresholds] = useState<RiskAppetiteThreshold[]>([]);
  const [notifications, setNotifications] = useState<NotificationRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'thresholds' | 'notifications' | 'analytics'>('thresholds');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(propProjectId || null);
  const [availableProjects, setAvailableProjects] = useState<any[]>([]);

  const [newThreshold, setNewThreshold] = useState<Partial<RiskAppetiteThreshold>>({
    threshold_name: '',
    risk_category: '',
    risk_level: 'MEDIUM',
    threshold_value: 0,
    threshold_type: 'count',
    metric_target: 'high_risks',
    measurement_period: 'monthly',
    tolerance_buffer: 10,
    auto_escalate: true,
    notification_enabled: true,
    escalation_recipients: [],
    business_unit: ''
  });

  const [newNotification, setNewNotification] = useState<Partial<NotificationRule>>({
    trigger_condition: 'threshold_exceeded',
    notification_type: 'email',
    recipients: [],
    message_template: '',
    cooldown_period: 24,
    active: true
  });

  useEffect(() => {
    if (!propProjectId) {
      // In Admin context, fetch available projects
      fetchAvailableProjects();
    }
    fetchConfiguration();
  }, [propProjectId, selectedProjectId]);

  const fetchAvailableProjects = async () => {
    try {
      console.log('Fetching projects...');
      const response = await api.get('/api/projects');
      console.log('Projects API response:', response);
      console.log('Response data:', response.data);

      // The projects are nested under 'projects' key in the response
      const projects = Array.isArray(response.data?.projects) ? response.data.projects : [];
      console.log('Extracted projects:', projects);
      setAvailableProjects(projects);

      // Auto-select first project if none selected and projects exist
      if (!selectedProjectId && projects.length > 0) {
        setSelectedProjectId(projects[0].id.toString());
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setAvailableProjects([]); // Ensure it's always an array
      toastService.error('Failed to load projects');
    }
  };

  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      const currentProjectId = propProjectId || selectedProjectId;
      const [thresholdsRes, notificationsRes] = await Promise.all([
        api.get(`/api/risk-appetite/thresholds${currentProjectId ? `?project_id=${currentProjectId}` : ''}`),
        api.get(`/api/risk-appetite/notifications${currentProjectId ? `?project_id=${currentProjectId}` : ''}`)
      ]);

      setThresholds(thresholdsRes.data || []);
      setNotifications(notificationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch risk appetite configuration:', error);
      toastService.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const generateAIThresholds = async () => {
    const currentProjectId = propProjectId || selectedProjectId;
    setIsGeneratingAI(true);
    try {
      const response = await api.post('/api/ai/generate-risk-appetite-thresholds', {
        project_id: currentProjectId,
        existing_thresholds: thresholds,
        industry: 'financial_services' // Could be dynamic
      });

      if (response.data && response.data.thresholds) {
        const aiThresholds = response.data.thresholds.map((threshold: any) => ({
          ...threshold,
          escalation_recipients: [],
          auto_escalate: true,
          notification_enabled: true
        }));

        setThresholds(prev => [...prev, ...aiThresholds]);
        toastService.success('AI-generated risk appetite thresholds added');
      }
    } catch (error) {
      console.error('Failed to generate AI thresholds:', error);

      // Only create fallback thresholds if we have a valid project ID
      if (!currentProjectId) {
        toastService.error('Please select a project to generate fallback thresholds');
        return;
      }

      // Fallback AI-generated thresholds - Create them in database
      const projectIdNum = parseInt(currentProjectId);
      const fallbackThresholds = [
        {
          threshold_name: 'High Operational Risk Limit',
          risk_category: 'Operational Risk',
          risk_level: 'HIGH',
          threshold_value: 5,
          threshold_type: 'count',
          metric_target: 'high_risks',
          measurement_period: 'monthly',
          tolerance_buffer: 20,
          auto_escalate: true,
          notification_enabled: true,
          escalation_recipients: ['risk.manager@company.com'],
          business_unit: 'Enterprise',
          project_id: projectIdNum
        },
        {
          threshold_name: 'Critical Compliance Risk Limit',
          risk_category: 'Compliance Risk',
          risk_level: 'CRITICAL',
          threshold_value: 0,
          threshold_type: 'count',
          metric_target: 'control_gaps',
          measurement_period: 'monthly',
          tolerance_buffer: 0,
          auto_escalate: true,
          notification_enabled: true,
          escalation_recipients: ['compliance@company.com', 'cro@company.com'],
          business_unit: 'Enterprise',
          project_id: projectIdNum
        },
        {
          threshold_name: 'Technology Risk Score Threshold',
          risk_category: 'Technology Risk',
          risk_level: 'HIGH',
          threshold_value: 85,
          threshold_type: 'score',
          metric_target: 'control_effectiveness',
          measurement_period: 'quarterly',
          tolerance_buffer: 15,
          auto_escalate: true,
          notification_enabled: true,
          escalation_recipients: ['it.security@company.com'],
          business_unit: 'IT Operations',
          project_id: projectIdNum
        }
      ];

      // Create fallback thresholds in database to get proper IDs
      try {
        const createdThresholds: RiskAppetiteThreshold[] = [];
        for (const thresholdData of fallbackThresholds) {
          const response = await api.post('/api/risk-appetite/thresholds', thresholdData);
          createdThresholds.push(response.data);
        }

        setThresholds(prev => [...prev, ...createdThresholds]);
        toastService.success('AI-generated risk appetite thresholds added (fallback)');
      } catch (fallbackError) {
        console.error('Failed to create fallback thresholds:', fallbackError);
        toastService.error('Failed to generate fallback thresholds');
      }
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const saveThreshold = async () => {
    const currentProjectId = propProjectId || selectedProjectId;

    if (!newThreshold.threshold_name || !newThreshold.risk_category || !newThreshold.threshold_value || !currentProjectId) {
      toastService.error('Please fill in all required fields and select a project');
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/api/risk-appetite/thresholds', {
        ...newThreshold,
        project_id: parseInt(currentProjectId)
      });

      setThresholds(prev => [...prev, response.data]);
      setNewThreshold({
        threshold_name: '',
        risk_category: '',
        risk_level: 'MEDIUM',
        threshold_value: 0,
        threshold_type: 'count',
        metric_target: 'high_risks',
        measurement_period: 'monthly',
        tolerance_buffer: 10,
        auto_escalate: true,
        notification_enabled: true,
        escalation_recipients: [],
        business_unit: ''
      });

      toastService.success('Risk appetite threshold saved');
      onConfigChange?.(response.data);
    } catch (error) {
      console.error('Failed to save threshold:', error);
      toastService.error('Failed to save threshold');
    } finally {
      setSaving(false);
    }
  };

  const deleteThreshold = async (id: number | undefined) => {
    if (!id || id === undefined) {
      toastService.error('Cannot delete threshold: Invalid ID');
      return;
    }

    try {
      await api.delete(`/api/risk-appetite/thresholds/${id}`);
      setThresholds(prev => prev.filter(t => t.id !== id));
      toastService.success('Threshold deleted');
    } catch (error) {
      console.error('Failed to delete threshold:', error);
      toastService.error('Failed to delete threshold');
    }
  };

  const updateThreshold = async (id: number | undefined, updates: Partial<RiskAppetiteThreshold>) => {
    if (!id || id === undefined) {
      toastService.error('Cannot update threshold: Invalid ID');
      return;
    }

    try {
      const response = await api.patch(`/api/risk-appetite/thresholds/${id}`, updates);
      setThresholds(prev => prev.map(t => t.id === id ? response.data : t));
      toastService.success('Threshold updated');
    } catch (error) {
      console.error('Failed to update threshold:', error);
      toastService.error('Failed to update threshold');
    }
  };

  const addRecipient = (field: 'escalation_recipients', email: string) => {
    if (email && !newThreshold.escalation_recipients?.includes(email)) {
      setNewThreshold(prev => ({
        ...prev,
        escalation_recipients: [...(prev.escalation_recipients || []), email]
      }));
    }
  };

  const removeRecipient = (field: 'escalation_recipients', email: string) => {
    setNewThreshold(prev => ({
      ...prev,
      escalation_recipients: prev.escalation_recipients?.filter(e => e !== email) || []
    }));
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="bg-[#221551] text-white px-6 py-4 rounded-t-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Risk Appetite Configuration
          </h3>
          <button
            onClick={generateAIThresholds}
            disabled={isGeneratingAI}
            className="flex items-center gap-2 px-3 py-1 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors text-sm disabled:opacity-50"
          >
            <Brain className={`h-4 w-4 ${isGeneratingAI ? 'animate-spin' : ''}`} />
            {isGeneratingAI ? 'Generating...' : 'AI Generate Thresholds'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <div className="flex">
          {[
            { id: 'thresholds', label: 'Risk Thresholds', icon: <AlertTriangle className="h-4 w-4" /> },
            { id: 'notifications', label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
            { id: 'analytics', label: 'Analytics', icon: <TrendingUp className="h-4 w-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-6 py-4 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-b-2 border-[#57C8F4] text-[#57C8F4] bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Project Selector - Only show in Admin context */}
        {!propProjectId && (
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Select Project</h4>
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
            >
              <option value="">Select a project...</option>
              {Array.isArray(availableProjects) && availableProjects.map((project) => (
                <option key={project.id} value={project.id.toString()}>
                  {project.project_name || project.name || `Project ${project.id}`}
                </option>
              ))}
            </select>
            {!selectedProjectId && (
              <p className="text-sm text-amber-600 mt-2">
                Please select a project to configure risk appetite thresholds.
              </p>
            )}
          </div>
        )}

        {/* Thresholds Tab */}
        {activeTab === 'thresholds' && (
          <div className="space-y-6">
            {/* Add New Threshold */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-4 flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                Add Risk Appetite Threshold
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Threshold Name *
                  </label>
                  <input
                    type="text"
                    value={newThreshold.threshold_name}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, threshold_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="e.g., High Operational Risk Limit"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Category *
                  </label>
                  <select
                    value={newThreshold.risk_category}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, risk_category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="">Select Category</option>
                    <option value="Operational Risk">Operational Risk</option>
                    <option value="Compliance Risk">Compliance Risk</option>
                    <option value="Technology Risk">Technology Risk</option>
                    <option value="Credit Risk">Credit Risk</option>
                    <option value="Market Risk">Market Risk</option>
                    <option value="Liquidity Risk">Liquidity Risk</option>
                    <option value="Reputational Risk">Reputational Risk</option>
                    <option value="Strategic Risk">Strategic Risk</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metric Target *
                  </label>
                  <select
                    value={newThreshold.metric_target}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, metric_target: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="high_risks">High Risks Count</option>
                    <option value="control_gaps">Control Gaps</option>
                    <option value="residual_risk_score">Residual Risk Score</option>
                    <option value="control_effectiveness">Control Effectiveness</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Level *
                  </label>
                  <select
                    value={newThreshold.risk_level}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, risk_level: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Threshold Value *
                  </label>
                  <input
                    type="number"
                    value={newThreshold.threshold_value}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, threshold_value: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Threshold Type
                  </label>
                  <select
                    value={newThreshold.threshold_type}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, threshold_type: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="count">Count</option>
                    <option value="percentage">Percentage</option>
                    <option value="score">Score</option>
                    <option value="monetary">Monetary</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Measurement Period
                  </label>
                  <select
                    value={newThreshold.measurement_period}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, measurement_period: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tolerance Buffer (%)
                  </label>
                  <input
                    type="number"
                    value={newThreshold.tolerance_buffer}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, tolerance_buffer: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Unit
                  </label>
                  <input
                    type="text"
                    value={newThreshold.business_unit}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, business_unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="e.g., Trading, Compliance, IT"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escalation Recipients
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter email address"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addRecipient('escalation_recipients', (e.target as HTMLInputElement).value);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.querySelector('input[type="email"]') as HTMLInputElement;
                        if (input?.value) {
                          addRecipient('escalation_recipients', input.value);
                          input.value = '';
                        }
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                  {newThreshold.escalation_recipients && newThreshold.escalation_recipients.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {newThreshold.escalation_recipients.map((email, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {email}
                          <button
                            onClick={() => removeRecipient('escalation_recipients', email)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex items-center gap-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newThreshold.auto_escalate}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, auto_escalate: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Auto-escalate breaches</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newThreshold.notification_enabled}
                    onChange={(e) => setNewThreshold(prev => ({ ...prev, notification_enabled: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Enable notifications</span>
                </label>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={saveThreshold}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Threshold'}
                </button>
              </div>
            </div>

            {/* Existing Thresholds */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-4 w-4 mr-2" />
                Configured Thresholds ({thresholds.length})
              </h4>

              {thresholds.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No risk appetite thresholds configured yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {thresholds.map((threshold, index) => (
                    <div key={threshold.id || index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-medium text-gray-900">{threshold.risk_category}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getRiskLevelColor(threshold.risk_level)}`}>
                              {threshold.risk_level}
                            </span>
                            <span className="text-sm text-gray-600">
                              {threshold.business_unit && `• ${threshold.business_unit}`}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Threshold:</span>
                              <span className="ml-2 font-medium">
                                {threshold.threshold_value} {threshold.threshold_type}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Period:</span>
                              <span className="ml-2 font-medium">{threshold.measurement_period}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Buffer:</span>
                              <span className="ml-2 font-medium">{threshold.tolerance_buffer}%</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Recipients:</span>
                              <span className="ml-2 font-medium">{threshold.escalation_recipients?.length || 0}</span>
                            </div>
                          </div>

                          <div className="mt-2 flex items-center gap-4 text-xs">
                            {threshold.auto_escalate && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800">
                                Auto-escalate enabled
                              </span>
                            )}
                            {threshold.notification_enabled && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                Notifications enabled
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {threshold.id && (
                            <>
                              <button
                                onClick={() => updateThreshold(threshold.id, { notification_enabled: !threshold.notification_enabled })}
                                className={`p-2 rounded-md transition-colors ${
                                  threshold.notification_enabled
                                    ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                                title={threshold.notification_enabled ? 'Disable notifications' : 'Enable notifications'}
                              >
                                <Bell className="h-4 w-4" />
                              </button>

                              <button
                                onClick={() => deleteThreshold(threshold.id)}
                                className="p-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                                title="Delete threshold"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {!threshold.id && (
                            <span className="text-xs text-gray-500 px-2 py-1 bg-yellow-100 rounded">
                              Creating...
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2 flex items-center">
                <Bell className="h-4 w-4 mr-2" />
                Notification Management
              </h4>
              <p className="text-sm text-yellow-800">
                Configure automated notifications for risk appetite threshold breaches and trend alerts.
              </p>
            </div>

            <div className="text-center py-8 text-gray-500">
              Advanced notification configuration coming soon...
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                Risk Appetite Analytics
              </h4>
              <p className="text-sm text-purple-800">
                Monitor trends, analyze threshold effectiveness, and optimize your risk appetite framework.
              </p>
            </div>

            <div className="text-center py-8 text-gray-500">
              Risk appetite analytics dashboard coming soon...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAppetiteConfig;